"use client"
import Paper from "@mui/material/Paper"
import { useTheme } from "@mui/material/styles"
import MuiTable from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableContainer from "@mui/material/TableContainer"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import type { SxProps } from "@mui/system"
import map from "lodash-es/map"
import { FC, ReactNode, lazy, Suspense, memo, useCallback } from "react"

import { Skeleton } from "../skeleton"

import type {
  TableProps,
  TableRowProps,
  TableHeaderCellProps,
  TableBodyCellProps,
} from "./Table.model"

import styles from "./Table.module.scss"

import { create } from "@/helpers/bem"

const bem = create(styles, "Table")

const Icon = lazy(() =>
  import("../icon").then((module) => ({ default: module.Icon })),
)

export const Table: FC<TableProps> = memo(
  ({
    variant = "primary",
    className,
    ariaLabel,
    caption,
    headerType = "single",
    headerProps = {},
    bodyProps = {},
    data,
    ...props
  }) => {
    const theme = useTheme()
    const dark = theme.palette.mode === "dark"
    const header = data?.header
    const body = data?.body

    const renderTableBodyCell = useCallback(
      (props: TableBodyCellProps, index: number): ReactNode => {
        let cellProps = props
        if (headerType === "double") {
          cellProps = {
            ...props,
            scope: "row",
            component: "th",
          }
        }
        let content = props?.label as ReactNode
        const icon = props?.icon
        if (icon) {
          content = (
            <Suspense fallback={<Skeleton variant="rounded" />}>
              <Icon
                size="sm"
                {...icon}
                color={theme.palette?.[icon?.color ?? "inherit"].main}
              />
            </Suspense>
          )
        }
        const sxTableBodyCell = {
          backgroundColor: theme.palette?.["backgroundWhite"].main,
          borderColor: theme.palette?.["lightGray"].main,
          fontSize: 15,
          color: theme.palette?.["black"].main,
        } as SxProps
        return (
          <TableCell
            key={`table-body-cell-${index}`}
            align={index === 0 ? "left" : "center"}
            itemProp="tableCell"
            sx={sxTableBodyCell}
            {...cellProps}
          >
            {content}
          </TableCell>
        )
      },
      [dark, headerType],
    )

    if (header?.length === 0 || body?.length === 0) return null

    const sxTable = {
      borderRadius: 3,
      overflow: "hidden",
    } as SxProps
    const sxTableHead = {
      background: theme.gradiant?.[variant],
    } as SxProps
    const sxTableHeadCell = {
      fontWeight: 700,
      fontSize: 16,
      color: dark
        ? theme.palette?.["inherit"].contrastText
        : theme.palette?.["inherit"].main,
    } as SxProps
    return (
      <TableContainer
        itemScope
        component={Paper}
        itemType="https://schema.org/Table"
      >
        <MuiTable
          {...props}
          aria-label={ariaLabel}
          className={bem(undefined, undefined, className)}
          sx={sxTable}
        >
          <caption
            className={bem("caption", {
              [theme.palette.mode]: true,
            })}
          >
            {caption}
          </caption>
          <TableHead sx={sxTableHead} {...headerProps}>
            <TableRow>
              {map(header, (cell: TableHeaderCellProps, a: number) => (
                <TableCell
                  key={`table-header-cell-${a}`}
                  align={a === 0 ? "left" : "center"}
                  itemProp="tableHeader"
                  sx={sxTableHeadCell}
                  {...cell}
                >
                  {cell?.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody {...bodyProps}>
            {map(body, ({ data, ...row }: TableRowProps, a: number) => (
              <TableRow
                key={`table-body-row-${a}`}
                itemScope
                itemProp="tableRow"
                itemType="https://schema.org/TableRow"
                {...row}
              >
                {map(data, (cell: TableBodyCellProps, b: number) =>
                  renderTableBodyCell(cell, b),
                )}
              </TableRow>
            ))}
          </TableBody>
        </MuiTable>
      </TableContainer>
    )
  },
)

Table.displayName = "Table"
